<script lang="ts" context="module">
    export enum TagItemState {
        DEFAULT = "default",
        EDITING = "editing",
        DELETING = "deleting",
    }
</script>

<script lang="ts">
    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { createEventDispatcher, onMount } from "svelte";
    import { tooltip } from "$/utils/tooltip";
    import { truncateText } from "$/utils/Utils"

    const dispatcher = createEventDispatcher<{ active: HTMLElement }>();

    export let tagName: string;
    export let tagId;
    export let active = false;
    export let onClick = () => {};
    export let editingTagId;
    export let onStartEditing = (id) => {};
    export let onStopEditing = () => {};
    export let isEditable = false;
    export let element: HTMLElement;

    let newTagName: string;
    let errorMessage = "";
    let renameInput: HTMLInputElement;

    let state: TagItemState = TagItemState.DEFAULT;

    $: {
        if (editingTagId === tagId) {
            state = TagItemState.EDITING;
        } else {
            state = TagItemState.DEFAULT;
        }
    }

    $: showUndoArrow = newTagName !== tagName;

    $: {
        if (active) {
            // wait for next tick make sure layout is ready
            requestAnimationFrame(() => dispatcher("active", element));
        }
    }

    async function renameTag(event: Event) {

        if (newTagName === tagName) {
            event?.stopImmediatePropagation(); 
            onStopEditing();
            return;
        }
        const validationError = validateTagRename(newTagName, tagName);

        if (validationError) {
            event?.stopImmediatePropagation(); 
            errorMessage = validationError;
            if (validationError === "Tag name already exists") renameInput.focus();
            return;
        }

        try {
            await ActiveTopicStore.renameTag(tagId, newTagName);
            state = TagItemState.DEFAULT;
            onStopEditing();
        } catch (error) {
            console.error("Failed to rename tag:", error);
            errorMessage = "Something went wrong";
        }
    }

    function validateTagRename(newTag, oldTag) {
        if (!newTag) return "Empty name provided";
        if ($ActiveTopic?.tags?.some((tag) => tag.tag === newTag)) {
            return "Tag name already exists";
        }
        return null; 
    }

    function toggleIsEditing() {
        if (state !== TagItemState.EDITING) {
            onStartEditing(tagId);
            newTagName = tagName; 
            errorMessage = "";
        } else {
            onStopEditing();
        }
    }

    async function deleteTag() {
        await ActiveTopicStore.deleteTag(tagId);
        state = TagItemState.DEFAULT;
    }

    function undoRenaming() {
        newTagName = tagName;
        onStopEditing();
        errorMessage = "";
    }

    onMount(async () => {
        newTagName = tagName;
    });

    function onNameChangeKeyPress(event) {
        if (event.key === "Enter") {
            renameTag(event);
        }
    }
</script>

<div
    on:click|stopPropagation={onClick}
    bind:this={element}
    class="tag-item group"
    class:active
>
    {#if state === TagItemState.DEFAULT}
        <div class="default-modal">
            <p class="tag-name">#{tagName}</p>

            {#if !editingTagId && isEditable}
                <button
                    class="settings-icon"
                    on:click|stopPropagation={toggleIsEditing}
                    use:tooltip={"Edit Tag"}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z"
                        />
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                    </svg>
                </button>
            {/if}
        </div>
    {:else if state === TagItemState.EDITING}
        <div class="editing-modal">
            <span class="rename-hash" class:rename-hash--warning={errorMessage}>
                #
            </span>
            <input
                class="rename-input"
                class:rename-input--warning={errorMessage}
                type="tagName"
                bind:value={newTagName}
                on:keypress|stopPropagation={onNameChangeKeyPress}
                on:click|stopPropagation
                tabindex="0"
                bind:this={renameInput}
                autofocus
            />

            {#if errorMessage}
                <svg
                    class="rename-icon"
                    use:tooltip={errorMessage}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    on:click|stopPropagation
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                    />
                </svg>
            {/if}

            <div class="edit-buttons">
                <button
                    class="edit-icon"
                    class:edit-icon--hidden={!showUndoArrow}
                    use:tooltip={"Undo"}
                    on:click|stopPropagation={undoRenaming}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
                        />
                    </svg>
                </button>
                <button
                    class="edit-icon"
                    use:tooltip={"Save"}
                    on:click={renameTag}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="m4.5 12.75 6 6 9-13.5"
                        />
                    </svg>
                </button>
                <button
                    class="edit-icon"
                    on:click|stopPropagation={() =>
                        (state = TagItemState.DELETING)}
                    use:tooltip={"Delete"}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                        />
                    </svg>
                </button>
            </div>
        </div>
    {:else if state === TagItemState.DELETING}
        <div class="delete-modal">
            <span class="delete-modal__message">Delete <b> #{truncateText(tagName, 12)} </b> ?</span
            >
            <div class="delete-btns">
                <button
                    class="delete-btns__btn delete-btns__btn--confirm"
                    on:click={() => deleteTag()}
                >
                    Delete
                </button>
                <button class="delete-btns__btn"> Cancel </button>
            </div>
        </div>
    {/if}
</div>

<style>
    @import "./../../../../main.css";

    .tag-item {
        @apply relative flex h-auto w-full cursor-pointer select-none items-center justify-start rounded border-none text-black outline-none hover:bg-gray-100;
    }

    .tag-item.active {
        @apply bg-gray-100;
    }
    .tag-name {
        @apply m-0 truncate px-0 text-left text-sm leading-6;
    }
    .settings-icon {
        @apply ml-auto hidden h-6 w-6 cursor-pointer items-center justify-center rounded-sm p-1 hover:bg-gray-300 group-hover:flex;
    }
    .settings-icon svg {
        @apply h-4 w-4;
    }

    .editing-modal {
        @apply flex h-full w-full items-center bg-gray-100 pl-1;
    }
    .editing-modal--warning {
        @apply bg-pink-100;
    }
    .rename-input {
        @apply h-6 rounded border-none bg-transparent px-0 py-0 text-sm text-black outline-none focus:ring-0;
    }
    .rename-input--warning {
        @apply bg-pink-100;
    }
    .rename-icon {
        @apply h-6 w-6 bg-pink-100 p-1 text-pink-600;
    }
    .rename-hash {
        @apply text-left text-sm leading-6;
    }
    .rename-hash--warning {
        @apply bg-pink-100;
    }
    .edit-icon {
        @apply flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm p-1 hover:bg-gray-300;
    }
    .edit-icon--hidden {
        @apply hidden;
    }
    .edit-icon svg {
        @apply h-4 w-4;
    }
    .edit-buttons {
        @apply ml-auto flex flex-row items-center gap-0.5;
    }

    .default-modal {
        @apply flex h-full w-full items-center pl-1;
    }

    .delete-modal {
        @apply flex h-full w-full flex-row items-center justify-start bg-pink-100;
    }
    .delete-modal__message {
        @apply h-full p-1 align-middle text-xs;
    }
    .delete-btns {
        @apply ml-auto flex flex-row justify-end self-end align-middle;
    }
    .delete-btns__btn {
        @apply rounded-sm px-2 py-1 text-xs text-black hover:bg-pink-200;
    }
    .delete-btns__btn--confirm {
        @apply font-bold text-pink-500;
    }
</style>
